import { useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Notify } from '../services/notificationService';

/**
 * Custom hook for secure token validation that prevents race conditions
 */
export const useSecureToken = () => {
  const { getAccessTokenSilently, isLoading, isAuthenticated } = useAuth0();
  const tokenCache = useRef<{ token: string | null; timestamp: number }>({
    token: null,
    timestamp: 0,
  });

  const validateAndGetToken = useCallback(async (): Promise<string | null> => {
    // Prevent operations during Auth0 loading state
    if (isLoading) {
      Notify.error(
        'Authentication in progress. Please wait a moment and try again.',
        3000
      );
      return null;
    }

    // Ensure user is authenticated
    if (!isAuthenticated) {
      Notify.error(
        'Authentication required. Please log in and try again.',
        3000
      );
      return null;
    }

    try {
      // Check cache first (valid for 30 seconds to prevent excessive calls)
      const now = Date.now();
      const cacheValidDuration = 30 * 1000; // 30 seconds

      if (
        tokenCache.current.token &&
        now - tokenCache.current.timestamp < cacheValidDuration
      ) {
        return tokenCache.current.token;
      }

      // Get fresh token from Auth0
      const token = await getAccessTokenSilently({
        cacheMode: 'on', // Use cached token if available and valid
        timeoutInSeconds: 10, // Prevent hanging requests
      });

      if (!token) {
        throw new Error('Failed to obtain access token');
      }

      // Update cache
      tokenCache.current = {
        token,
        timestamp: now,
      };

      return token;
    } catch (error) {
      console.warn('Token validation failed:', error);

      // Clear invalid token from cache
      tokenCache.current = { token: null, timestamp: 0 };

      Notify.error(
        'Failed to validate authentication. Please refresh the page and try again.',
        5000
      );
      return null;
    }
  }, [getAccessTokenSilently, isLoading, isAuthenticated]);

  const clearTokenCache = useCallback(() => {
    tokenCache.current = { token: null, timestamp: 0 };
  }, []);

  return {
    validateAndGetToken,
    clearTokenCache,
    isAuthLoading: isLoading,
    isAuthenticated,
  };
};
